import { Badge, Container, Grid, Group, Loader, Paper, SimpleGrid, Table, Text, Title } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { PageHeader } from "@/components/PageHeader"
import { queries } from "@/lib/queries"

const PLAYFAIR = "'Playfair Display', Georgia, serif"

export const Route = createFileRoute("/")({
  component: PortfolioDashboard,
})

function fmt(n: number) {
  return n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(0)}M` : `$${n.toLocaleString()}`
}

function PortfolioDashboard() {
  const summary = useQuery(queries.portfolioSummary())
  const milestones = useQuery(queries.portfolioMilestones())
  const riskMatrix = useQuery(queries.portfolioRiskMatrix())
  const scenarios = useQuery(queries.portfolioScenarios())

  return (
    <Container size="xl">
      <PageHeader eyebrow="Syndicate I">Portfolio Dashboard</PageHeader>

      {summary.isPending ? (
        <Loader />
      ) : summary.isError ? (
        <Text c="var(--pitch-negative)">Failed to load summary</Text>
      ) : (
        <SimpleGrid cols={{ base: 2, md: 4 }} mb="xl">
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Text fz={11} lts="0.08em" c="var(--pitch-text-secondary)" tt="uppercase" fw={600}>
              Total Commitment
            </Text>
            <Text ff={PLAYFAIR} fz={26} fw={500} c="var(--pitch-text-primary)" mt={6} style={{ lineHeight: 1.1 }}>
              {fmt(summary.data.totalCommitment)}
            </Text>
            <Text fz={12} c="var(--pitch-text-tertiary)" mt={4}>
              {fmt(summary.data.totalAllocated)} allocated
              {summary.data.totalCommitment > summary.data.totalAllocated
                ? ` · ${fmt(summary.data.totalCommitment - summary.data.totalAllocated)} reserve`
                : ""}
            </Text>
          </Paper>
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Text fz={11} lts="0.08em" c="var(--pitch-text-secondary)" tt="uppercase" fw={600}>
              Total Called
            </Text>
            <Text ff={PLAYFAIR} fz={26} fw={500} c="var(--pitch-text-primary)" mt={6} style={{ lineHeight: 1.1 }}>
              {fmt(summary.data.totalCalled)}
            </Text>
          </Paper>
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Text fz={11} lts="0.08em" c="var(--pitch-text-secondary)" tt="uppercase" fw={600}>
              Fee Structure
            </Text>
            <Text ff={PLAYFAIR} fz={20} fw={500} c="var(--pitch-text-primary)" mt={6} style={{ lineHeight: 1.2 }}>
              {summary.data.feeStructure}
            </Text>
          </Paper>
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Text fz={11} lts="0.08em" c="var(--pitch-text-secondary)" tt="uppercase" fw={600}>
              Companies
            </Text>
            <Text ff={PLAYFAIR} fz={26} fw={500} c="var(--pitch-text-primary)" mt={6} style={{ lineHeight: 1.1 }}>
              {summary.data.companyCount}
            </Text>
          </Paper>
        </SimpleGrid>
      )}

      <Grid mb="xl">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Title
              order={4}
              ff={PLAYFAIR}
              fw={500}
              c="var(--pitch-text-primary)"
              mb="sm"
              style={{ letterSpacing: "-0.005em" }}
            >
              Milestone Timeline
            </Title>
            {milestones.isPending ? (
              <Loader size="sm" />
            ) : milestones.isError ? (
              <Text c="var(--pitch-negative)" size="sm">
                Failed to load milestones
              </Text>
            ) : milestones.data.length === 0 ? (
              <Text c="var(--pitch-text-tertiary)" size="sm">
                No milestones data
              </Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Company</Table.Th>
                    <Table.Th>Milestones</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {milestones.data.map((row) => (
                    <Table.Tr key={row.companyId}>
                      <Table.Td fw={600}>{row.companyName}</Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          {row.milestones.map((m) => (
                            <Badge key={`${m.quarter}-${m.code}`} size="xs" variant="light">
                              {m.quarter}: {m.code}
                            </Badge>
                          ))}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Title
              order={4}
              ff={PLAYFAIR}
              fw={500}
              c="var(--pitch-text-primary)"
              mb="sm"
              style={{ letterSpacing: "-0.005em" }}
            >
              Risk Matrix
            </Title>
            {riskMatrix.isPending ? (
              <Loader size="sm" />
            ) : riskMatrix.isError ? (
              <Text c="var(--pitch-negative)" size="sm">
                Failed to load risk data
              </Text>
            ) : riskMatrix.data.length === 0 ? (
              <Text c="var(--pitch-text-tertiary)" size="sm">
                No risk ratings data
              </Text>
            ) : (
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Company</Table.Th>
                    <Table.Th>Ratings</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {riskMatrix.data.map((row) => (
                    <Table.Tr key={row.companyId}>
                      <Table.Td fw={600}>{row.companyName}</Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          {Object.entries(row.ratings).map(([cat, rating]) => (
                            <Badge
                              key={cat}
                              size="xs"
                              color={
                                rating === "green"
                                  ? "pitchPositive"
                                  : rating === "yellow"
                                    ? "pitchInflection"
                                    : "pitchNegative"
                              }
                            >
                              {cat}
                            </Badge>
                          ))}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {scenarios.data && (
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
              <Title
                order={4}
                ff={PLAYFAIR}
                fw={500}
                c="var(--pitch-text-primary)"
                mb="sm"
                style={{ letterSpacing: "-0.005em" }}
              >
                Return Scenarios
              </Title>
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Scenario</Table.Th>
                    <Table.Th>MOIC</Table.Th>
                    <Table.Th>Gross Return</Table.Th>
                    <Table.Th>Net Return</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {scenarios.data.scenarios.map((s) => (
                    <Table.Tr key={s.scenarioName}>
                      <Table.Td fw={600}>{s.scenarioName}</Table.Td>
                      <Table.Td className="pitch-tabular">{s.grossMoic}x</Table.Td>
                      <Table.Td className="pitch-tabular">${s.grossReturnM}M</Table.Td>
                      <Table.Td className="pitch-tabular">${s.netReturnM}M</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
              <Title
                order={4}
                ff={PLAYFAIR}
                fw={500}
                c="var(--pitch-text-primary)"
                mb="sm"
                style={{ letterSpacing: "-0.005em" }}
              >
                DPI Waterfall
              </Title>
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Year</Table.Th>
                    <Table.Th>Cumulative DPI</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {scenarios.data.dpiWaterfall.map((d) => (
                    <Table.Tr key={d.year}>
                      <Table.Td className="pitch-tabular">{d.year}</Table.Td>
                      <Table.Td className="pitch-tabular">{d.cumulativeDpi}x</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </Grid.Col>
        </Grid>
      )}
    </Container>
  )
}
